<script setup lang="ts">
import { Label } from '@progress/kendo-vue-labels';
import { ErrorObject } from '@vuelidate/core';
import { computed } from 'vue';

type Errors = ErrorObject & { $params: object & { message: string }};

// Props.
interface Props {
  dataTestid?: string,
  label?: string,
  info?: string,
  warning?: string,
  errors?: Errors[] | ErrorObject[],
  required?: boolean,
  requiredLevel?: 1 | 2,
  for?: string,
}

const props = withDefaults(defineProps<Props>(), {
  dataTestid: 'label',
  label: '',
  info: undefined,
  warning: undefined,
  errors: () => ([]),
  required: false,
  requiredLevel: 1,
  for: undefined,
});

// Computed
const errors = computed(() => {
  const list: string[] = [];
  props.errors.forEach((error) => {
    list.push(error.$params.message);
  });

  return list.join('<br>');
});

const asterisks = computed(() => '*'.repeat(props.requiredLevel));
</script>

<template>
  <Label
    :for="props.for"
    :data-testid="`${props.dataTestid}_label`"
    class="flex align-items-center gap-1 cursor-pointer mr-2"
    :class="{ 'text-error': props.errors?.length }"
  >
    <span>{{ props.label }}<sup v-if="props.required">{{ asterisks }}</sup></span>

    <span
      v-if="props.info"
      class="fas fa-info-circle text-info"
      v-tooltip="{ content: props.info, theme: 'info'}"
      :data-testid="`${props.dataTestid}_info_icon`"
    />

    <span
      v-if="props.warning"
      class="fas fa-exclamation-square text-warning"
      v-tooltip="{ content: props.warning, theme: 'warning'}"
      :data-testid="`${props.dataTestid}_warning_icon`"
    />

    <span
      v-if="props.errors?.length"
      class="far fa-exclamation-circle text-error"
      v-tooltip="{ content: errors, theme: 'error'}"
      :data-testid="`${props.dataTestid}_error_icon`"
    />
  </Label>
</template>
